const STORAGE_KEY = 'ac:wishlist:v1';
const STORAGE_VERSION = 1;
const DEFAULT_MAX_ITEMS = 24;
const HARD_MAX_ITEMS = 50;
const PRODUCT_FETCH_CONCURRENCY = 4;
const CHANGE_EVENT = 'ac:wishlist:change';
const OPEN_EVENT = 'ac:wishlist:open';
/** @type {WishlistRecord[]} */
let memoryWishlist = [];
let activeLimit = DEFAULT_MAX_ITEMS;

/** @typedef {{id: string, handle: string}} WishlistRecord */

/** @param {string} currency @returns {Intl.NumberFormat} */
function createCurrencyFormatter(currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency || 'USD',
    });
  } catch {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: 'USD',
    });
  }
}

/** @returns {Storage | null} */
function getWishlistStorage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/** @param {unknown} value @returns {WishlistRecord[]} */
function normalizeWishlist(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  return value
    .filter((item) => item && /^\d+$/.test(String(item.id)) && /^[a-z0-9][a-z0-9-]*$/i.test(String(item.handle)))
    .map((item) => ({ id: String(item.id), handle: String(item.handle) }))
    .filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    })
    .slice(0, activeLimit);
}

/** @returns {WishlistRecord[]} */
function readWishlist() {
  const storage = getWishlistStorage();
  if (!storage) return memoryWishlist.slice();

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) {
      memoryWishlist = [];
      return [];
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      storage.removeItem(STORAGE_KEY);
      memoryWishlist = [];
      return [];
    }

    const isLegacyPayload = Array.isArray(parsed);
    const items = isLegacyPayload
      ? parsed
      : parsed && parsed.version === STORAGE_VERSION && Array.isArray(parsed.items)
        ? parsed.items
        : null;

    if (!items) {
      storage.removeItem(STORAGE_KEY);
      memoryWishlist = [];
      return [];
    }

    const normalized = normalizeWishlist(items);
    memoryWishlist = normalized;

    // The first release stored a bare array. Migrate it in place while keeping
    // the same versioned key so future payload changes can be handled explicitly.
    if (isLegacyPayload) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, items: normalized }));
      } catch {
        // Keep the normalized list available in memory if migration is blocked.
      }
    }

    return normalized.slice();
  } catch {
    return memoryWishlist.slice();
  }
}

/** @param {WishlistRecord[]} items */
function writeWishlist(items) {
  const normalized = normalizeWishlist(items);
  memoryWishlist = normalized;
  const storage = getWishlistStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, items: normalized }));
    } catch {
      // Wishlist remains a non-blocking enhancement when storage is unavailable.
    }
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: normalized, count: normalized.length } }));
}

/** @param {number} requestedLimit */
function setLimit(requestedLimit) {
  activeLimit = Math.max(1, Math.min(Number(requestedLimit) || DEFAULT_MAX_ITEMS, HARD_MAX_ITEMS));
  const items = readWishlist();
  if (items.length > activeLimit) writeWishlist(items.slice(0, activeLimit));
}

class AcWishlistButton extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelector('button')?.addEventListener('click', this.#toggle, { signal });
    document.addEventListener(CHANGE_EVENT, this.#sync, { signal });
    window.addEventListener('storage', this.#handleStorage, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #handleStorage = (event) => {
    if (event.key === STORAGE_KEY) this.#sync();
  };

  #toggle = () => {
    const id = String(this.dataset.productId || '');
    const handle = String(this.dataset.productHandle || '');
    if (!/^\d+$/.test(id) || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return;

    const items = readWishlist();
    const existingIndex = items.findIndex((item) => item.id === id);
    const added = existingIndex < 0;
    if (!added) {
      items.splice(existingIndex, 1);
    } else {
      items.unshift({ id, handle });
    }
    writeWishlist(items);

    if (added && this.dataset.openAfterAdd === 'true') {
      document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { trigger: this.querySelector('button') } }));
    }
  };

  #sync = () => {
    const button = this.querySelector('button');
    if (!button) return;

    const selected = readWishlist().some((item) => item.id === String(this.dataset.productId || ''));
    const label = selected ? this.dataset.removeLabel : this.dataset.addLabel;
    button.setAttribute('aria-pressed', String(selected));
    button.setAttribute('aria-label', label || '');
    this.dataset.selected = String(selected);

    const visibleLabel = this.querySelector('[data-ac-wishlist-label]');
    if (visibleLabel) visibleLabel.textContent = label || '';
  };
}

class AcWishlistDrawer {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #renderAbortController = null;
  /** @type {number} */
  #renderVersion = 0;
  /** @type {Map<string, any>} */
  #productCache = new Map();
  /** @type {boolean} */
  #focusAfterRemoval = false;

  /** @param {HTMLElement & { isOpen?: boolean, open?: () => void, close?: () => void }} drawer */
  constructor(drawer) {
    this.drawer = drawer;
  }

  connect() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    setLimit(Number(this.drawer.dataset.limit));

    this.drawer.querySelector('[data-ac-wishlist-open]')?.addEventListener('click', this.#open, { signal });
    this.drawer.querySelector('[data-ac-wishlist-close]')?.addEventListener('click', this.#close, { signal });
    this.drawer.addEventListener('theme-drawer:open', this.#handleDrawerOpen, { signal });
    this.drawer.addEventListener('theme-drawer:close', this.#handleDrawerClose, { signal });
    this.drawer.querySelector('[data-ac-wishlist-list]')?.addEventListener('click', this.#handleListClick, { signal });
    document.addEventListener(CHANGE_EVENT, this.#handleChange, { signal });
    document.addEventListener(OPEN_EVENT, this.#handleOpenRequest, { signal });
    window.addEventListener('storage', this.#handleStorage, { signal });
    this.#updateCount();

    if (this.drawer.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnect() {
    this.#cancelRender();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #cancelRender() {
    this.#renderVersion += 1;
    this.#renderAbortController?.abort();
    this.#renderAbortController = null;
    this.drawer.removeAttribute('aria-busy');
  }

  #handleStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;
    this.#updateCount();
    if (this.drawer.isOpen) this.#renderItems();
  };

  #handleChange = () => {
    this.#updateCount();
    if (this.drawer.isOpen) this.#renderItems();
  };

  #open = (event) => {
    event.preventDefault();
    this.drawer.open?.();
  };

  #handleOpenRequest = (event) => {
    const trigger = event.detail?.trigger;
    if (trigger instanceof HTMLElement && document.contains(trigger)) trigger.focus();
    this.drawer.open?.();
  };

  #close = (event) => {
    event.preventDefault();
    this.drawer.close?.();
  };

  #handleDrawerOpen = () => {
    this.drawer.querySelector('[data-ac-wishlist-open]')?.setAttribute('aria-expanded', 'true');
    this.#renderItems();
  }

  #handleDrawerClose = () => {
    this.drawer.querySelector('[data-ac-wishlist-open]')?.setAttribute('aria-expanded', 'false');
    this.#cancelRender();
  };

  #handleListClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ac-wishlist-remove]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    if (document.activeElement === button) this.#focusAfterRemoval = true;
    writeWishlist(readWishlist().filter((item) => item.id !== button.dataset.productId));
  };

  #updateCount() {
    const count = readWishlist().length;
    const countNode = this.drawer.querySelector('[data-ac-wishlist-count]');
    const trigger = this.drawer.querySelector('[data-ac-wishlist-open]');
    if (countNode) countNode.textContent = count ? `(${count})` : '';
    if (trigger) {
      const openLabel = this.drawer.dataset.openLabel || '';
      trigger.setAttribute('aria-label', count ? `${openLabel} (${count})` : openLabel);
    }
  }

  async #renderItems() {
    const version = ++this.#renderVersion;
    this.#renderAbortController?.abort();
    const renderAbortController = new AbortController();
    this.#renderAbortController = renderAbortController;
    const records = readWishlist();
    const list = this.drawer.querySelector('[data-ac-wishlist-list]');
    const empty = this.drawer.querySelector('[data-ac-wishlist-empty]');
    const status = this.drawer.querySelector('[data-ac-wishlist-status]');
    if (!(list instanceof HTMLElement) || !(empty instanceof HTMLElement)) {
      if (this.#renderAbortController === renderAbortController) this.#renderAbortController = null;
      return;
    }

    list.replaceChildren();
    empty.hidden = records.length > 0;
    if (records.length === 0) {
      if (status) status.textContent = '';
      this.#restoreRemovalFocus();
      if (this.#renderAbortController === renderAbortController) this.#renderAbortController = null;
      return;
    }

    this.drawer.setAttribute('aria-busy', 'true');
    if (status) status.textContent = this.drawer.dataset.loadingLabel || '';

    const results = [];
    try {
      for (let index = 0; index < records.length; index += PRODUCT_FETCH_CONCURRENCY) {
        const batch = records.slice(index, index + PRODUCT_FETCH_CONCURRENCY);
        results.push(...await Promise.all(batch.map((record) => this.#getProduct(record, renderAbortController.signal))));
        if (version !== this.#renderVersion || !this.drawer.isConnected) return;
      }

      results.forEach(({ product }, index) => list.append(this.#createItem(records[index], product)));
      if (status) {
        const failedCount = results.filter(({ failed }) => failed).length;
        status.textContent = failedCount ? this.drawer.dataset.unavailableLabel || '' : '';
      }
    } finally {
      if (this.#renderAbortController !== renderAbortController) return;

      this.#renderAbortController = null;
      this.drawer.removeAttribute('aria-busy');
      this.#restoreRemovalFocus();
    }
  }

  #restoreRemovalFocus() {
    if (!this.#focusAfterRemoval) return;
    this.#focusAfterRemoval = false;
    const target = this.drawer.querySelector('[data-ac-wishlist-remove]') || this.drawer.querySelector('[data-ac-wishlist-close]');
    if (target instanceof HTMLElement) target.focus();
  }

  /** @param {WishlistRecord} record */
  async #getProduct(record, signal) {
    if (this.#productCache.has(record.handle)) {
      return { product: this.#productCache.get(record.handle), failed: false };
    }

    const root = (this.drawer.dataset.rootUrl || '/').replace(/\/?$/, '/');
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(record.handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal,
      });
      if (!response.ok) throw new Error(`Product request failed with ${response.status}`);
      const product = await response.json();
      this.#productCache.set(record.handle, product);
      return { product, failed: false };
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[ac-wishlist] Unable to load product:', error);
      return { product: null, failed: error?.name !== 'AbortError' };
    }
  }

  /** @param {WishlistRecord} record @param {any} product */
  #createItem(record, product) {
    const item = document.createElement('li');
    item.className = 'ac-wishlist-drawer__item';

    const root = (this.drawer.dataset.rootUrl || '/').replace(/\/?$/, '/');
    const productLink = document.createElement(product ? 'a' : 'div');
    productLink.className = 'ac-wishlist-drawer__product';
    if (product) {
      productLink.href = `${root}products/${encodeURIComponent(record.handle)}`;
    } else {
      productLink.classList.add('ac-wishlist-drawer__product--unavailable');
    }

    if (product?.featured_image) {
      const image = document.createElement('img');
      image.className = 'ac-wishlist-drawer__image';
      image.src = product.featured_image;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 120;
      image.height = 120;
      productLink.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'ac-wishlist-drawer__image--placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      productLink.append(placeholder);
    }

    const details = document.createElement('span');
    details.className = 'ac-wishlist-drawer__details';
    const title = document.createElement('span');
    title.className = 'ac-wishlist-drawer__title';
    title.textContent = product?.title || record.handle.replaceAll('-', ' ');
    details.append(title);

    if (Number.isFinite(Number(product?.price))) {
      const price = document.createElement('span');
      price.className = 'ac-wishlist-drawer__price';
      price.textContent = createCurrencyFormatter(this.drawer.dataset.currency || 'USD').format(Number(product.price) / 100);
      details.append(price);
    } else if (!product) {
      const unavailable = document.createElement('span');
      unavailable.className = 'ac-wishlist-drawer__price';
      unavailable.textContent = this.drawer.dataset.unavailableLabel || '';
      details.append(unavailable);
    }

    productLink.append(details);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button-unstyled ac-wishlist-drawer__remove';
    remove.dataset.acWishlistRemove = '';
    remove.dataset.productId = record.id;
    remove.setAttribute('aria-label', `${this.drawer.dataset.removeLabel || ''} ${title.textContent}`.trim());
    remove.textContent = '×';

    item.append(productLink, remove);
    return item;
  }
}

if (!customElements.get('ac-wishlist-button')) customElements.define('ac-wishlist-button', AcWishlistButton);

const wishlistDrawers = new WeakMap();
const wishlistDrawerElements = new Set();
const WISHLIST_GLOBAL_KEY = Symbol.for('ac:wishlist:global');

/** @param {ParentNode} root */
function getWishlistDrawers(root) {
  const drawers = [];
  if (root instanceof Element && root.matches('[data-ac-wishlist-drawer]')) drawers.push(root);
  drawers.push(...root.querySelectorAll?.('[data-ac-wishlist-drawer]') || []);
  return drawers;
}

/** @param {ParentNode} root */
function connectWishlistDrawers(root) {
  getWishlistDrawers(root).forEach((drawer) => {
    if (wishlistDrawers.has(drawer)) return;
    const controller = new AcWishlistDrawer(drawer);
    wishlistDrawers.set(drawer, controller);
    wishlistDrawerElements.add(drawer);
    controller.connect();
  });
}

/** @param {ParentNode} root */
function disconnectWishlistDrawers(root) {
  getWishlistDrawers(root).forEach((drawer) => {
    wishlistDrawers.get(drawer)?.disconnect();
    wishlistDrawers.delete(drawer);
    wishlistDrawerElements.delete(drawer);
  });
}

function disconnectAllWishlistDrawers() {
  wishlistDrawerElements.forEach((drawer) => {
    wishlistDrawers.get(drawer)?.disconnect();
    wishlistDrawers.delete(drawer);
  });
  wishlistDrawerElements.clear();
}

function initializeWishlistDrawers() {
  globalThis[WISHLIST_GLOBAL_KEY]?.disconnect?.();

  const abortController = new AbortController();
  const { signal } = abortController;
  const globalController = {
    disconnect() {
      abortController.abort();
      disconnectAllWishlistDrawers();
    },
  };

  globalThis[WISHLIST_GLOBAL_KEY] = globalController;

  customElements.whenDefined('theme-drawer').then(() => {
    if (!signal.aborted) connectWishlistDrawers(document);
  });
  document.addEventListener('shopify:section:load', (event) => connectWishlistDrawers(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => disconnectWishlistDrawers(event.target), { signal });
}

initializeWishlistDrawers();
